import Image from "@Components/Image";
import styles from "./ShareProperty.module.scss";
import config from "@Config/index";
import { PAGE, getPropertyShareSingularLink } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useState } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsApp from "hooks/useIsApp";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import useCustomWindowOpen from "@Components/CustomWindowOpen";

const ShareProperty = ({
  inventoryUrl,
  isDesktop,
  canonicalName,
}: {
  inventoryUrl: string;
  isDesktop?: boolean;
  canonicalName?: string;
}) => {
  const customWindowOpen = useCustomWindowOpen();
  const { t } = useTranslationHook();
  const [copyText, setText] = useState(() => t("propertypage.headerdesktop.copylink"));
  const isApp = useIsApp();

  const singularLink = isApp && canonicalName ? getPropertyShareSingularLink(canonicalName) : null;
  const shareUrl = singularLink || inventoryUrl;
  const encodedShareUrl = encodeURIComponent(shareUrl);

  const facebookShareUrl = `https://facebook.com/sharer.php?u=${encodedShareUrl}`;
  const emailShareUrl = `mailto:?subject=Check out this property&body=${encodedShareUrl}`;
  const smsShareUrl = `sms:?body=${encodedShareUrl}`;

  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const whatsappShareUrl = /(android|iphone|ipad|mobile)/i.test(userAgent)
    ? `https://api.whatsapp.com/send?text=${encodedShareUrl}`
    : `https://web.whatsapp.com/send?text=${encodedShareUrl}`;
  const linkedInShareUrl = `https://linkedin.com/shareArticle?url=${encodedShareUrl}`;

  function handleOnClick() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setText("Link Copied");
      setTimeout(() => {
        setText("Copy Link");
      }, 3000);
    });

    gtagEventEmitter({
      action: "clicked_share",
      category: PAGE.INVENTORY,
      label: "copy",
    });
  }

  const handleShareClick = (text: string, url: string) => {
    gtagEventEmitter({
      action: "clicked_share",
      category: PAGE.INVENTORY,
      label: text,
    });

    if (isApp) {
      sendWebViewEvent(WEB_VIEW_APP_EVENTS.OPEN_EXTERNAL_URL, { url });
    } else if (text === "Email" || text === "SMS") {
      customWindowOpen({ url, target: "_self", isExternal: true });
    } else {
      customWindowOpen({ url, target: "_blank", isExternal: true });
    }
  };

  // const { t } = useTranslationHook();

  return (
    <div className={styles.container}>
      <p className={styles.details}>
        {t("propertypage.components.shareproperty.checkoutthisproperty")}
      </p>
      <div className={formatClassNames(styles.shareList, isDesktop && styles.desktopShareList)}>
        <div
          data-testid="property-share-link-Copylink"
          className={styles.shareItem}
          role="none"
          onClick={handleOnClick}
        >
          <Image
            src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/documents.svg`}
            width={14}
            height={14}
            alt="document"
          />
          {t(copyText)}
        </div>
        <div
          data-testid="property-share-link-Email"
          className={styles.shareItem}
          role="none"
          onClick={() => handleShareClick("Email", emailShareUrl)}
        >
          <Image
            src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/envelope.svg`}
            width={14}
            height={14}
            alt="envelope"
          />
          {t("propertypage.components.shareproperty.email")}
        </div>
        <div
          data-testid="property-share-link-SMS"
          className={styles.shareItem}
          role="none"
          onClick={() => handleShareClick("SMS", smsShareUrl)}
        >
          <Image
            src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/enquire.svg`}
            width={14}
            height={14}
            alt="enquire"
          />
          {t("propertypage.components.shareproperty.sms")}
        </div>
        {!config.IS_CHINA_APP && (
          <>
            <div
              data-testid="property-share-link-facebook"
              className={styles.shareItem}
              role="none"
              onClick={() => handleShareClick("Facebook", facebookShareUrl)}
            >
              <Image
                src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/facebook.svg`}
                width={14}
                height={14}
                alt="facebook"
              />
              {t("propertypage.components.shareproperty.facebook")}
            </div>
            <div
              data-testid="property-share-link-linkedin"
              className={styles.shareItem}
              role="none"
              onClick={() => handleShareClick("LinkedIn", linkedInShareUrl)}
            >
              <Image
                src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/linkedin-in.svg`}
                width={14}
                height={14}
                alt="linkedin"
              />
              {t("propertypage.components.shareproperty.linkedin")}
            </div>
            <div
              data-testid="property-share-link-whatsapp"
              className={styles.shareItem}
              role="none"
              onClick={() => handleShareClick("Whatsapp", whatsappShareUrl)}
            >
              <Image
                src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/whatsapp.svg`}
                width={14}
                height={14}
                alt="whatsapp"
              />
              {t("propertypage.components.shareproperty.whatsapp")}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(ShareProperty, {
  componentName: "ShareProperty-PropertyPage",
  showForChina: true,
});
